(() => {
  if (customElements.get('wih-size-guide')) return;
  const element = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  class WihSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('.wih-guide__trigger');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.trigger.addEventListener('click', event => {
        event.preventDefault();
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
        this.load();
      });
      this.querySelector('.wih-guide__close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.trigger.focus({ preventScroll: true });
      });
    }
    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow || '';
      }
    }
    async load() {
      if (this.loaded || this.loading) return;
      this.loading = true;
      const body = this.querySelector('.wih-guide__body');
      const status = this.querySelector('.wih-guide__loading');
      const error = this.querySelector('.wih-guide__error');
      status.hidden = false;
      error.hidden = true;
      body.setAttribute('aria-busy', 'true');
      try {
        const source = this.querySelector('.wih-guide__kiwi-source');
        if (!source) throw new Error('Kiwi chart source missing');
        await new Promise((resolve, reject) => {
          const ready = () => source.querySelector('table') && source.querySelector('.ks-table-footer') && source.querySelector('.ks-html-content');
          if (ready()) return resolve();
          const observer = new MutationObserver(() => {
            if (ready()) { observer.disconnect(); clearTimeout(timer); resolve(); }
          });
          const timer = setTimeout(() => { observer.disconnect(); reject(new Error('Kiwi chart unavailable')); }, 20000);
          observer.observe(source, { childList: true, subtree: true });
        });
        const chart = source.querySelector('table');
        const rows = Array.from(chart.rows).map(row => Array.from(row.cells).map(cell => cell.textContent.trim()));
        if (rows.length < 2 || rows.some(row => row.length !== 2)) throw new Error('Invalid Kiwi chart');
        const title = source.querySelector('.ks-table-header')?.textContent.trim() || 'Size guide';
        this.querySelector('h2').textContent = title;
        const fragment = document.createDocumentFragment();
        const table = element('table', null, 'wih-guide__table');
        table.setAttribute('aria-label', title);
        const head = element('thead');
        const header = element('tr');
        rows.shift().forEach(text => { const cell = element('th', text); cell.scope = 'col'; header.append(cell); });
        head.append(header);
        const tbody = element('tbody');
        rows.forEach(row => { const tr = element('tr'); row.forEach(text => tr.append(element('td', text))); tbody.append(tr); });
        table.append(head, tbody);
        fragment.append(table);
        const iconBase = 'https://assets.replocdn.com/projects/2dd0b3d6-6cf0-48be-9c23-e3efd4937738/';
        const note = (text, kind, iconFile) => {
          if (!text) return;
          const card = element('div', null, 'wih-guide__note ' + kind);
          if (kind === 'wih-guide__measure') card.append(element('h3', 'HOW TO MEASURE'));
          if (iconFile) {
            const icon = element('img', null, 'wih-guide__note-icon');
            icon.src = iconBase + iconFile + '?fit=contain';
            icon.alt = '';
            icon.setAttribute('aria-hidden', 'true');
            icon.decoding = 'async';
            icon.addEventListener('error', () => { icon.remove(); card.classList.remove('wih-guide__note--illustrated'); });
            card.classList.add('wih-guide__note--illustrated');
            card.append(icon);
          }
          const copy = element('div', null, 'wih-guide__note-copy');
          copy.append(element('p', text));
          card.append(copy);
          fragment.append(card);
        };
        const footer = source.querySelector('.ks-table-footer').textContent.trim();
        const strap = footer.match(/Strap width:\s*([0-9]+)\s*mm\.?/i);
        note(strap ? footer.replace(strap[0], '').trim() : footer, 'wih-guide__fit');
        if (strap) note('Strap width: ' + strap[1] + ' mm', 'wih-guide__strap', 'c9e493fc-0cb9-44fb-9ab3-2495a7182058');
        const paragraphs = Array.from(source.querySelectorAll('.ks-html-content p')).map(p => p.textContent.trim()).filter(Boolean);
        const measureIndex = paragraphs.findIndex(text => text === 'HOW TO MEASURE');
        if (measureIndex !== -1) note(paragraphs[measureIndex + 1], 'wih-guide__measure', 'f03e83ed-9630-490c-ab78-818e57f3930b');
        const unsure = paragraphs.find(text => text.startsWith('Still unsure?'));
        note(unsure, 'wih-guide__unsure');
        this.querySelector('.wih-guide__content').replaceChildren(fragment);
        this.loaded = true;
      } catch (reason) {
        error.hidden = false;
      } finally {
        status.hidden = true;
        body.setAttribute('aria-busy', 'false');
        this.loading = false;
      }
    }
  }
  customElements.define('wih-size-guide', WihSizeGuide);
})();

/* Inline link: shows "Size - Size guide" next to the Size label and hides the stand-alone link (Greg, 17 Oct 2026) */
(() => {
  const place = () => {
    const orig = document.querySelector('wih-size-guide .wih-guide__trigger');
    if (!orig) return;
    const label = Array.from(document.querySelectorAll('.variant__label')).find(l => /^\s*(shoe\s+)?size\s*$/i.test(l.firstChild ? l.firstChild.textContent : '') && l.offsetParent !== null) ||
                  Array.from(document.querySelectorAll('.variant__label')).find(l => /^\s*(shoe\s+)?size\s*(\u2014.*)?$/i.test(l.textContent) && l.offsetParent !== null);
    if (!label) return;
    if (label.querySelector('.wih-guide__inline')) return;
    const sep = document.createElement('span');
    sep.className = 'wih-guide__sep';
    sep.textContent = ' \u2014 ';
    const link = document.createElement('a');
    link.className = 'wih-guide__inline';
    link.href = orig.href;
    link.textContent = orig.textContent.trim() || 'Size guide';
    link.addEventListener('click', event => { event.preventDefault(); orig.click(); });
    label.appendChild(sep);
    label.appendChild(link);
    document.documentElement.classList.add('wih-guide-inline');
  };
  const start = () => {
    place();
    setTimeout(place, 800);
    setTimeout(place, 2500);
    const wrap = document.querySelector('.variant-wrapper') && document.querySelector('.variant-wrapper').parentElement;
    if (wrap && window.MutationObserver) new MutationObserver(() => place()).observe(wrap, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
